import { Container, Graphics, Text } from 'pixi.js';
import { type WorldEventKind, type WorldZone } from '@duck/contracts';
import { isReducedMotion } from './reduced-motion';

/**
 * Procedural rendering of the SHARED random world events (M7b "Play Together" — emotion: Tradition).
 * Every so often the server flips `worldEvent` on the authoritative room state (like `timeOfDay`/
 * `weather`, M4.2) and the WHOLE office sees the same spontaneous moment at the same time — a pizza
 * party or a (friendly) fire drill. It's a reason to GATHER, never a task and never tracked
 * (Constitution Art. 1). Per ADR-0010 these visuals are CODE-AUTHORED Pixi primitives — no art assets.
 *
 * NOTE (ADR-0031): the DISCO is no longer one of these world-wide events. It's now PLAYER-DRIVEN and
 * ROOM-LOCAL — toggled by the 🎵 Jukebox and rendered per-room by {@link DiscoZoneLayer}, not here.
 *
 * PERFORMANCE is a first-class gate, exactly like {@link AmbientLayer}:
 *   • the per-kind decor is built ONCE on transition (in {@link setEvent}) and reused while the event
 *     runs — {@link update} only animates scale/alpha/position, never `new`s anything per frame;
 *   • only ONE event is ever active, so at most one kind's nodes exist at a time.
 *
 * ACCESSIBILITY is the other gate. This layer is DECORATIVE and lives BELOW the ducks/labels/badges,
 * so its props sit on the FLOOR only — the foreground stays fully legible. Reduced motion → a correct
 * STATIC frame (no pulsing/bobbing). The dual-coded (icon + TITLE + blurb) read-out + the polite
 * announcement live in the DOM HUD, never here.
 *
 * Teardown: when the event returns to 'none' (or changes) we DESTROY the prior kind's nodes so nothing
 * lingers; the engine's `app.destroy(..., {children:true})` also tears `root` down on shutdown.
 */

export class WorldEventLayer {
  /**
   * Added by the engine BELOW the ducks (between the ambient layer and the actors) so event visuals
   * sit on the floor and never occlude or dim the foreground (ducks, labels, badges, the HUD).
   */
  readonly root = new Container();

  /** The kind currently built into the scene. We rebuild only on a real change (idempotent setEvent). */
  private kind: WorldEventKind = 'none';

  // ── Pizza: a static lounge prop (table + pizza + slices), built once on transition. ──
  private pizzaRoot: Container | null = null;

  // ── Fire drill: a pulsing "gather here" ring + 🧯 marker at a meeting point. ──
  private fireRoot: Container | null = null;
  private fireRing: Graphics | null = null;
  private fireIcon: Text | null = null;

  /** Monotonic seconds for animation phases — advanced from dt, never `performance.now()` (no alloc). */
  private clock = 0;

  /**
   * The resolved level's zones + world size (Phase 3), so a shared event anchors in a sensible room of the
   * ACTUAL world (the office lounge / open-space, or — when those zones don't exist, e.g. the castle — the
   * world centre). Built once per level by the engine after it resolves `levelId`.
   */
  constructor(
    private readonly zones: readonly WorldZone[],
    private readonly worldWidth: number,
    private readonly worldHeight: number,
  ) {}

  /** Where an event anchors: the centre of zone `id` in THIS level, or the world centre if it has none. */
  private zoneCenter(id: string): { x: number; y: number } {
    const z = this.zones.find((zone) => zone.id === id);
    if (!z) return { x: this.worldWidth / 2, y: this.worldHeight / 2 };
    return { x: z.x + z.width / 2, y: z.y + z.height / 2 };
  }

  /**
   * Switch to the active shared event (idempotent). Builds the new kind's procedural decor and tears
   * down the previous kind's nodes so nothing from a finished event lingers. Cheap: called only when
   * the authoritative `worldEvent` actually changes (the engine compares before calling).
   */
  setEvent(kind: WorldEventKind): void {
    if (kind === this.kind) return;
    // Tear the OUTGOING kind down (props are destroyed so nothing lingers when they're inactive).
    this.teardownPizza();
    this.teardownFire();

    this.kind = kind;

    if (kind === 'pizza') this.buildPizza();
    if (kind === 'fire_drill') this.buildFire();
  }

  /** Per-frame animation of the active event (scale/alpha/position only — no allocations). */
  update(dtMs: number): void {
    const dt = Math.min(dtMs, 50) / 1000;
    this.clock += dt;
    const reduced = isReducedMotion();

    // ── Fire drill: a pulsing ring + a gently bobbing 🧯 (static under reduced motion). ──
    if (this.fireRoot && this.fireRing) {
      if (reduced) {
        this.fireRing.scale.set(1);
        this.fireRing.alpha = 0.85;
        if (this.fireIcon) this.fireIcon.y = FIRE_ICON_Y;
      } else {
        const pulse = 0.5 + 0.5 * Math.sin(this.clock * 3);
        this.fireRing.scale.set(1 + pulse * 0.18);
        this.fireRing.alpha = 0.55 + pulse * 0.4;
        if (this.fireIcon) this.fireIcon.y = FIRE_ICON_Y - Math.sin(this.clock * 2.4) * 4;
      }
    }

    // Pizza is a static prop — nothing to animate. (A still table reads as "here it is", not noise.)
  }

  // ── Pizza ─────────────────────────────────────────────────────────────────────────────────────

  /**
   * Build the pizza-party prop in the LOUNGE (per the contract blurb "fresh pizza in the lounge"): a
   * round table with a whole pizza on top + a couple of slices, all Pixi primitives. A friendly, static
   * focal point that says "gather here" without animation noise. Destroyed when the event ends.
   */
  private buildPizza(): void {
    const { x, y } = this.zoneCenter('lounge');
    const root = new Container();
    root.x = x;
    root.y = y;

    // Table (a warm wooden round) with a soft drop shadow so it reads as a real object on the floor.
    const shadow = new Graphics().ellipse(0, 14, 64, 22).fill({ color: 0x000000, alpha: 0.22 });
    const table = new Graphics()
      .ellipse(0, 0, 60, 40)
      .fill(0x8a5a2b)
      .ellipse(0, -4, 52, 34)
      .fill(0xa9703a);
    root.addChild(shadow, table);

    // Whole pizza (crust + cheese + a ring of pepperoni). Deterministic dabs — no Math.random.
    const pizza = new Graphics().circle(0, -4, 30).fill(0xe9b84b).circle(0, -4, 26).fill(0xf4cf6e);
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * Math.PI * 2;
      pizza.circle(Math.cos(a) * 14, -4 + Math.sin(a) * 14, 4).fill(0xc0392b);
    }
    root.addChild(pizza);

    // A loose slice beside the table (the "grab a slice" cue): a triangle of crust + cheese + a dab.
    const slice = new Graphics()
      .poly([46, 6, 78, -6, 78, 18])
      .fill(0xf4cf6e)
      .poly([50, 7, 74, -1, 74, 13])
      .fill(0xe9b84b)
      .circle(64, 6, 3)
      .fill(0xc0392b);
    root.addChild(slice);

    // A small dual-purpose 🍕 label floating just above so it reads instantly even at a glance.
    const icon = new Text({
      text: '🍕',
      style: { fontSize: 26, fontFamily: 'ui-sans-serif, system-ui, sans-serif' },
    });
    icon.anchor.set(0.5);
    icon.y = -48;
    root.addChild(icon);

    this.pizzaRoot = root;
    this.root.addChild(root);
  }

  private teardownPizza(): void {
    if (!this.pizzaRoot) return;
    this.pizzaRoot.destroy({ children: true });
    this.pizzaRoot = null;
  }

  // ── Fire drill ────────────────────────────────────────────────────────────────────────────────

  /**
   * Build the fire-drill "gather here" marker at the OPEN SPACE centre (a roomy, central meeting point
   * everyone can reach): concentric rings (the outer one pulses) + a bobbing 🧯. Clearly says "all to
   * the meeting point, together" — friendly, never alarming (no red strobe; the pulse is gentle).
   */
  private buildFire(): void {
    const { x, y } = this.zoneCenter('open-space');
    const root = new Container();
    root.x = x;
    root.y = y;

    // A soft target on the floor: a filled disc + a steady inner ring + the pulsing outer ring.
    const pad = new Graphics()
      .circle(0, 0, 70)
      .fill({ color: 0xffd23f, alpha: 0.1 })
      .circle(0, 0, 44)
      .stroke({ color: 0xffd23f, width: 4, alpha: 0.5 });
    const ring = new Graphics().circle(0, 0, 60).stroke({ color: 0xffd23f, width: 5, alpha: 0.9 });
    root.addChild(pad, ring);

    const icon = new Text({
      text: '🧯',
      style: { fontSize: 34, fontFamily: 'ui-sans-serif, system-ui, sans-serif' },
    });
    icon.anchor.set(0.5);
    icon.y = FIRE_ICON_Y;
    root.addChild(icon);

    this.fireRoot = root;
    this.fireRing = ring;
    this.fireIcon = icon;
    this.root.addChild(root);
  }

  private teardownFire(): void {
    if (!this.fireRoot) return;
    this.fireRoot.destroy({ children: true });
    this.fireRoot = null;
    this.fireRing = null;
    this.fireIcon = null;
  }

  /**
   * Release pooled references. The engine destroys the whole Pixi display tree on shutdown
   * (`app.destroy(..., {children:true})`), which tears down `root` and every child — so we only drop
   * our own references here (matching {@link AmbientLayer.destroy}), never double-destroy nodes.
   */
  destroy(): void {
    this.pizzaRoot = null;
    this.fireRoot = null;
    this.fireRing = null;
    this.fireIcon = null;
  }
}

/** Y offset of the 🧯 icon above the fire-drill ring (shared by build + the bobbing animation). */
const FIRE_ICON_Y = -2;
